// TourContext.js - v3.7.0
// Global context for managing guided tours across the application

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { tourAPI } from '../services/api';

const TourContext = createContext();

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  // Track which tours are currently running
  const [activeTours, setActiveTours] = useState({});
  // Cache of tour preferences
  const [preferences, setPreferences] = useState({});
  // Loading states
  const [loading, setLoading] = useState({});
  // Track fetched tours to avoid duplicate requests (using ref to avoid re-renders)
  const fetchedTours = useRef({});

  /**
   * Fetch tour preference from API
   */
  const fetchTourPreference = useCallback(async (tourId) => {
    // Check if already fetched or currently fetching
    if (fetchedTours.current[tourId]) {
      return null;
    }

    // Mark as fetching
    fetchedTours.current[tourId] = true;
    setLoading(prev => ({ ...prev, [tourId]: true }));

    try {
      const response = await tourAPI.getPreference(tourId);
      const data = response.data?.data || null;

      setPreferences(prev => ({ ...prev, [tourId]: data }));
      return data;
    } catch (error) {
      console.error(`Error fetching tour preference for ${tourId}:`, error);
      // Reset fetched flag on error to allow retry
      fetchedTours.current[tourId] = false;
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [tourId]: false }));
    }
  }, []); // No dependencies - function is stable

  /**
   * Check if a tour should auto-start
   */
  const shouldAutoStart = useCallback((tourId) => {
    const pref = preferences[tourId];
    // Auto-start if no preference exists or status is 'not_started'
    return !pref || pref.status === 'not_started';
  }, [preferences]);

  /**
   * Start a tour
   */
  const startTour = useCallback(async (tourId, fromStep = 0) => {
    setActiveTours(prev => ({ ...prev, [tourId]: { running: true, step: fromStep } }));

    // Update backend
    try {
      await tourAPI.saveProgress(tourId, { status: 'in_progress', current_step: fromStep });
      setPreferences(prev => ({
        ...prev,
        [tourId]: { ...prev[tourId], status: 'in_progress', current_step: fromStep }
      }));
    } catch (error) {
      console.error(`Error starting tour ${tourId}:`, error);
    }
  }, []);

  /**
   * Update tour progress (step change)
   */
  const updateProgress = useCallback(async (tourId, stepIndex) => {
    setActiveTours(prev => ({
      ...prev,
      [tourId]: { ...prev[tourId], step: stepIndex }
    }));

    // Update backend
    try {
      await tourAPI.saveProgress(tourId, { current_step: stepIndex });
      setPreferences(prev => ({
        ...prev,
        [tourId]: { ...prev[tourId], current_step: stepIndex }
      }));
    } catch (error) {
      console.error(`Error updating tour progress ${tourId}:`, error);
    }
  }, []);

  /**
   * Complete a tour
   */
  const completeTour = useCallback(async (tourId) => {
    setActiveTours(prev => ({ ...prev, [tourId]: { running: false, step: 0 } }));

    try {
      await tourAPI.complete(tourId);
      setPreferences(prev => ({
        ...prev,
        [tourId]: { ...prev[tourId], status: 'completed' }
      }));
    } catch (error) {
      console.error(`Error completing tour ${tourId}:`, error);
    }
  }, []);

  /**
   * Skip a tour
   */
  const skipTour = useCallback(async (tourId) => {
    setActiveTours(prev => ({ ...prev, [tourId]: { running: false, step: 0 } }));

    try {
      await tourAPI.skip(tourId);
      setPreferences(prev => ({
        ...prev,
        [tourId]: { ...prev[tourId], status: 'skipped' }
      }));
    } catch (error) {
      console.error(`Error skipping tour ${tourId}:`, error);
    }
  }, []);

  /**
   * Restart a tour
   */
  const restartTour = useCallback(async (tourId) => {
    try {
      await tourAPI.restart(tourId);
      setPreferences(prev => ({
        ...prev,
        [tourId]: { ...prev[tourId], status: 'not_started', current_step: 0 }
      }));
      // Start the tour immediately
      startTour(tourId, 0);
    } catch (error) {
      console.error(`Error restarting tour ${tourId}:`, error);
    }
  }, [startTour]);

  /**
   * Stop a tour without changing its saved status
   */
  const stopTour = useCallback((tourId) => {
    setActiveTours(prev => ({ ...prev, [tourId]: { running: false, step: 0 } }));
  }, []);

  /**
   * Check if a tour is currently running
   */
  const isTourRunning = useCallback((tourId) => {
    return activeTours[tourId]?.running || false;
  }, [activeTours]);

  /**
   * Get current step for a tour
   */
  const getCurrentStep = useCallback((tourId) => {
    return activeTours[tourId]?.step || 0;
  }, [activeTours]);

  const value = {
    // State
    activeTours,
    preferences,
    loading,

    // Actions
    fetchTourPreference,
    shouldAutoStart,
    startTour,
    updateProgress,
    completeTour,
    skipTour,
    restartTour,
    stopTour,

    // Getters
    isTourRunning,
    getCurrentStep
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
